import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TextInputModern, Button, Badge } from '@pavanhiro/common-ui';
import { Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validate email format
      if (!email || !/\S+@\S+\.\S+/.test(email)) {
        throw new Error('Please enter a valid email address');
      }

      // Call backend API to send reset email
      const response = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email. Please try again.');
      }

      // Show success message
      setSuccess(true);

      // Redirect to login after 5 seconds
      setTimeout(() => {
        navigate('/login');
      }, 5000);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <div className="auth-card">
          {/* Header */}
          <div className="auth-header">
            <div className="auth-logo">
              <div className="logo-icon">🔐</div>
              <h1 className="logo-text">ZoAuth Auth</h1>
            </div>
            <h2 className="auth-title">Reset Your Password</h2>
            <p className="auth-subtitle">
              {success
                ? "We've sent you a password reset link"
                : "Enter your email address and we'll send you a link to reset your password"}
            </p>
          </div>

          {/* Success State */}
          {success ? (
            <div className="success-state">
              <div className="success-icon-wrapper">
                <CheckCircle2 size={64} className="success-icon" />
              </div>
              <h3 className="success-title">Check Your Email</h3>
              <p className="success-message">
                We've sent a password reset link to <strong>{email}</strong>
              </p>
              <div className="success-instructions">
                <p>📧 Check your inbox (and spam folder)</p>
                <p>🔗 Click the reset link in the email</p>
                <p>🔒 Create a new secure password</p>
              </div>
              <Badge themeColor="info" variant="soft" className="success-badge">
                Didn't receive the email? Check your spam folder or contact support.
              </Badge>
              <div className="success-actions">
                <Button
                  variant="outline"
                  size="large"
                  onClick={() => navigate('/login')}
                  className="back-to-login-btn"
                >
                  <ArrowLeft size={18} />
                  Back to Login
                </Button>
              </div>
            </div>
          ) : (
            /* Form State */
            <form onSubmit={handleSubmit} className="auth-form">
              {/* Error Message */}
              {error && (
                <Badge themeColor="danger" variant="soft" className="auth-error-badge">
                  {error}
                </Badge>
              )}

              {/* Email Field */}
              <TextInputModern
                variant="email"
                placeholder="Enter your email address"
                value={email}
                onChange={(value) => setEmail(value)}
                required
                size="lg"
                styleType="outline"
                leftSection={<Mail size={20} />}
                autoComplete="email"
                disabled={loading}
              />

              {/* Submit Button */}
              <Button
                type="submit"
                variant="primary"
                size="large"
                loading={loading}
                className="auth-submit-btn"
              >
                {loading ? 'Sending Reset Link...' : 'Send Reset Link'}
              </Button>

              {/* Back to Login Link */}
              <div className="auth-footer-links">
                <Link to="/login" className="auth-link">
                  <ArrowLeft size={16} />
                  Back to Login
                </Link>
              </div>
            </form>
          )}

          {/* Security Note */}
          {!success && (
            <div className="auth-security-note">
              <p>
                🔒 <strong>Security Note:</strong> For your protection, we won't confirm
                whether this email is registered. If the email exists, you'll receive a
                reset link.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="auth-page-footer">
          <p>
            Need help? <a href="mailto:support@zoauth.com">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
